import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useReducer,
	useRef,
} from "react";
import { ViewportContext } from ".";
import message from "../../utils/message";
import useMessage, { MESSAGE_TYPES } from "../../hooks/useMessage";
import { isFunction } from "@disco/disco_core";
import debounce from "../../utils/debounce";
import useDimensions from "../../hooks/useDimensions";

export const VIEWPORT_MANAGER_REDUCER_TYPE = {
	ADD_CALLBACK: "ADD_CALLBACK",
	REMOVE_CALLBACK: "REMOVE_CALLBACK",
};
const viewportManagerReducer = (state, action) => {
	const { idx, type, callback } = action || {};
	switch (type) {
		case VIEWPORT_MANAGER_REDUCER_TYPE.ADD_CALLBACK: {
			return {
				...state,
				[idx]: callback,
			};
		}
		case VIEWPORT_MANAGER_REDUCER_TYPE.REMOVE_CALLBACK: {
			const callbacksCopy = { ...state };
			const filteredCallbacks = {};
			Object.entries(callbacksCopy)
				.filter(([internalIdx]) => internalIdx !== idx)
				.forEach(([internalIdx, callback]) => {
					filteredCallbacks[internalIdx] = callback;
				});
			return filteredCallbacks;
		}
		default: {
			console.warn("UNKNOWN ACTION TYPE DISPATCHED FOR VIEWPORT");
			return state;
		}
	}
};

const ViewportManager = memo(({ children }) => {
	const [callbacks, manageCallbacks] = useReducer(viewportManagerReducer, {});
	const mostRecentData = useRef(null);

	const handleParentStatsUpdate = useCallback(
		(data) => {
			if (!data) return;
			mostRecentData.current = data;
			Object.entries(callbacks).forEach(([, callback]) => {
				if (!isFunction(callback)) return;
				callback(data);
			});
		},
		[callbacks]
	);

	useMessage(MESSAGE_TYPES.UPDATE_PARENT_STATS, handleParentStatsUpdate);

	const triggerRecompute = useCallback(
		() => handleParentStatsUpdate(mostRecentData.current),
		[handleParentStatsUpdate]
	);

	const requestParentStats = useCallback(
		() => message(MESSAGE_TYPES.REQUEST_PARENT_STATS),
		[]
	);

	const args = useMemo(
		() => ({
			manageCallbacks,
			triggerGlobalRecompute: triggerRecompute,
			requestParentStats,
		}),
		[triggerRecompute, manageCallbacks, requestParentStats]
	);

	useEffect(() => {
		// We're routing through script, instead of directly triggering recompute
		// to prevent that as a dependency, and thus attaching event listener multiple times
		const handleScroll = debounce(() => {
			requestParentStats();
		}, 100);
		window.addEventListener("scroll", handleScroll);
		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, [requestParentStats]);

	// adding this logic to conditionally disable overscroll
	const { inDesktopOnly } = useDimensions();
	useEffect(() => {
		const htmlEl = document.querySelector("html");
		const bodyEl = document.querySelector("body");
		const configureOverScroll = ({ el, enable = true } = {}) => {
			if (!el || typeof el?.className !== "string") return;
			const isOverScrollConfigured =
				el.className.indexOf("disable-overscroll") !== -1;

			if (enable) {
				if (isOverScrollConfigured) return;
				el.className = `${el.className} disable-overscroll`.trim();
				return;
			}
			if (!enable) {
				if (!isOverScrollConfigured) return;
				el.className = `${el.className.replace(
					"disable-overscroll",
					""
				)}`.trim();
			}
		};
		if (inDesktopOnly) {
			configureOverScroll({ el: htmlEl, enable: false });
			configureOverScroll({ el: bodyEl, enable: false });
			return;
		}
		configureOverScroll({ el: htmlEl, enable: true });
		configureOverScroll({ el: bodyEl, enable: true });
		return;
	}, [inDesktopOnly]);

	return (
		<ViewportContext.Provider value={args}>
			{children}
		</ViewportContext.Provider>
	);
});

export default ViewportManager;
